import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import SortableTableHeader from './components/SortableTableHeader';
import downloadCsv from './utils/downloadCsv';
import type { OrganizationFacilitiesAndEmailsReportRecord } from './hooks/useOrganizations';
import type { FieldSortCriterionValue } from './utils/tableSort';

type OrganizationFacilitiesAndEmailsReportPageProps = {
    hasAdminReports: boolean;
    onSort: (fieldName: string) => void;
    organizations: OrganizationFacilitiesAndEmailsReportRecord[];
    sort: FieldSortCriterionValue;
};

export default function OrganizationFacilitiesAndEmailsReportPage({
    hasAdminReports,
    onSort,
    organizations = [],
    sort,
}: OrganizationFacilitiesAndEmailsReportPageProps) {
    const navigate = useNavigate();
    const [filter, setFilter] = useState('');

    const filteredOrganizations = useMemo(() => {
        const term = filter.trim().toLowerCase();
        if (!term) return organizations;
        return organizations.filter((org) => (
            String(org?.name || '').toLowerCase().includes(term) ||
            org?.facilities?.some((facility) => String(facility?.license || '').toLowerCase().includes(term)) ||
            org?.emailAddresses?.some((email) => String(email?.emailAddress || '').toLowerCase().includes(term))
        ));
    }, [organizations, filter]);

    function handleDownloadCsv() {
        const headers = ['Organization Name', 'Facilities', 'Email Addresses'];
        const rows = filteredOrganizations.map(org => ({
            'Organization Name': org.name,
            'Facilities': (org.facilities || []).map(f => f.license).join(', '),
            'Email Addresses': (org.emailAddresses || []).map(e => e.emailAddress).join(', '),
        }));
        downloadCsv(rows, 'organization-facilities-and-emails-report.csv', headers);
    }

    if (!hasAdminReports) {
        return (
            <div className="stack" data-testid="page-organizations-no-permission">
                <PageHeader
                    title="Organization Facilities and Emails Report"
                    actions={
                        <button className="button" type="button" onClick={() => navigate('/internal/reports')} data-testid="back-to-reports" style={{ alignSelf: 'flex-start' }}>
                            Back
                        </button>
                    }
                />
                <div className="card" style={{ padding: '2rem' }}>
                    <p className="muted">You do not have permission to view this report.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="stack" data-testid="organization-facilities-report-page">
            <PageHeader
                title="Organization Facilities and Emails Report"
                actions={
                    <div style={{ display: 'flex', gap: '0.5rem', alignSelf: 'flex-start' }}>
                        <button className="button" type="button" onClick={() => navigate('/internal/reports')} data-testid="back-to-reports">
                            Back
                        </button>
                        <button
                            className="button"
                            type="button"
                            onClick={handleDownloadCsv}
                            disabled={filteredOrganizations.length === 0}
                            data-testid="download-report-csv"
                        >
                            Download CSV
                        </button>
                    </div>
                }
            />

            <div className="card filter-card">
                <div className="filter-bar">
                    <div className="filter-left">
                        <input
                            className="input filter-search"
                            type="search"
                            aria-label="Filter by organization, license, or email"
                            placeholder="Filter by organization, license, or email"
                            value={filter}
                            onChange={(event) => setFilter(event.target.value)}
                            data-testid="report-filter-input"
                        />
                    </div>
                </div>
            </div>

            <table className="table">
                <thead>
                    <tr>
                        <SortableTableHeader label="Organization Name" fieldName="name" sort={sort} onSort={onSort} />
                        <SortableTableHeader label="Facilities" fieldName="facilities" sort={sort} onSort={onSort} />
                        <SortableTableHeader label="Email Addresses" fieldName="emailAddresses" sort={sort} onSort={onSort} />
                    </tr>
                </thead>
                <tbody>
                    {filteredOrganizations.length === 0 ? (
                        <tr>
                            <td colSpan={3} className="muted" style={{ textAlign: 'center' }}>
                                {filter.trim() ? 'No matching organizations' : 'No organizations to display'}
                            </td>
                        </tr>
                    ) : filteredOrganizations.map(org => (
                            <tr key={org.id}>
                                <td>{org.name}</td>
                                <td>{(org.facilities || []).map(facility => facility.license).join(', ')}</td>
                                <td>{(org.emailAddresses || []).map(email => email.emailAddress).join(', ')}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
}
